import {useData} from "@hps/hops-react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import {slugify} from "Raildays/RaildaysUtils.js";
import {useCallback, useMemo, useState} from "react";
import {useParams} from "react-router-dom";

import Button from "Components/Button.js";
import Card from "Components/Card.js";
import Container from "Components/Container.js";
import Flex from "Components/Flex.js";
import Html from "Components/Html.js";
import Link from "Components/Link.js";
import Loader from "Components/Loader.js";
import String from "Components/String.js";
import RaildaysService from "Services/RaildaysService.js";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CARD_BODY_PADDING = "clamp(16px, 2.2vw, 32px)";
const GOLD_COLOR = "#e2b534";
const PANEL_BORDER = "#e5e7eb";

const getQuestionCountLabel = count => (
	`${count} question${count === 1 ? "" : "s"}`
);

const renderAnswerText = answer => (
	(answer || "").replace(/\r?\n/g, "<br />")
);

const RailwayFaqs = ({topics}) => {
	const [expandedTopics, setExpandedTopics] = useState([]);
	const [expandedQuestion, setExpandedQuestion] = useState(false);

	const handleTopicChange = panel => (e, isExpanded) => {
		setExpandedTopics(prev => (
			isExpanded ?
				[...prev, panel] :
				prev.filter(item => item !== panel)
		));
	};

	const handleQuestionChange = panel => (e, isExpanded) => {
		setExpandedQuestion(isExpanded ? panel : false);
	};

	const questionCount = topics.reduce((total, topic) => (
		total + (Array.isArray(topic?.Questions) ? topic.Questions.length : 0)
	), 0);

	return (
		<Flex gap={3}>
			<Flex gap={0.5}>
				<String bold={true} noFlex={true} str="Frequently asked questions" variant="h5" />
				<String
					color="text.secondary"
					noFlex={true}
					str={`${topics.length} topics, ${getQuestionCountLabel(questionCount)}`} />
			</Flex>

			<Flex gap={0}>
				{topics.map((topic, topicIndex) => {
					const topicPanel = `${topic?.Id || topicIndex}`;
					const questions = Array.isArray(topic?.Questions) ? topic.Questions : [];
					const topicExpanded = expandedTopics.includes(topicPanel);

					return (
						<Accordion
							disableGutters={true}
							expanded={topicExpanded}
							key={topic?.Id || topic?.Name || topicIndex}
							onChange={handleTopicChange(topicPanel)}
							sx={{
								"border": `1px solid ${PANEL_BORDER}`,
								"borderRadius": 2,
								"boxShadow": "0 10px 28px rgba(15, 23, 42, 0.08)",
								"overflow": "hidden",
								"&:before": {display: "none"},
								"&.Mui-expanded": {
									mt: 0,
									mb: 0
								},
								"& + &": {
									mt: 2
								}
							}}>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon sx={{color: GOLD_COLOR}} />}
								sx={{
									"minHeight": 76,
									"px": {xs: 2, md: 3},
									"py": 1,
									"& .MuiAccordionSummary-content": {
										alignItems: "center",
										my: 0
									}
								}}>
								<Box
									sx={{
										alignItems: "center",
										display: "flex",
										gap: 2,
										justifyContent: "space-between",
										pr: 1,
										width: "100%"
									}}>
									<Flex gap={0.5}>
										<String
											bold={true}
											noFlex={true}
											str={topic?.Name || "General"}
											style={{lineHeight: 1.25}}
											variant="h6" />
										<String
											color="text.secondary"
											noFlex={true}
											str={getQuestionCountLabel(questions.length)}
											variant="body2" />
									</Flex>
									<Box
										sx={{
											alignItems: "center",
											bgcolor: "rgba(226, 181, 52, 0.16)",
											borderRadius: 999,
											color: "#6b530f",
											display: "inline-flex",
											fontSize: "1.05rem",
											fontWeight: 800,
											justifyContent: "center",
											lineHeight: 1,
											minHeight: 40,
											minWidth: 64,
											px: 2
										}}>
										{questions.length}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails
								sx={{
									bgcolor: "#f8fafc",
									borderTop: `1px solid ${PANEL_BORDER}`,
									px: {xs: 2, md: 3},
									py: {xs: 2, md: 3}
								}}>
								<Flex gap={0}>
									{questions.map((faq, questionIndex) => {
										const questionPanel = `${topicPanel}-${faq?.QuestionId || questionIndex}`;

										return (
											<Accordion
												disableGutters={true}
												elevation={0}
												expanded={expandedQuestion === questionPanel}
												key={questionPanel}
												onChange={handleQuestionChange(questionPanel)}
												sx={{
													"border": `1px solid ${PANEL_BORDER}`,
													"borderRadius": 1.5,
													"overflow": "hidden",
													"&:before": {display: "none"},
													"&.Mui-expanded": {
														mt: 0,
														mb: 0
													},
													"& + &": {
														mt: 1.25
													}
												}}>
												<AccordionSummary
													expandIcon={<ExpandMoreIcon />}
													sx={{
														"minHeight": 58,
														"px": {xs: 2, md: 2.5},
														"& .MuiAccordionSummary-content": {my: 1}
													}}>
													<String
														bold={true}
														noFlex={true}
														str={faq.QuestionText}
														style={{lineHeight: 1.35}} />
												</AccordionSummary>
												<AccordionDetails
													sx={{
														borderTop: `1px solid ${PANEL_BORDER}`,
														color: "text.secondary",
														px: {xs: 2, md: 2.5},
														py: 2
													}}>
													<Html content={renderAnswerText(faq.AnswerText)} />
												</AccordionDetails>
											</Accordion>
										);
									})}
								</Flex>
							</AccordionDetails>
						</Accordion>
					);
				})}
			</Flex>
		</Flex>
	);
};

const RailwayFaqsView = () => {
	const {slug} = useParams();

	const {data: railwaysData, error: railwaysError, loading: railwaysLoading} = useData(
		useCallback(() => RaildaysService.getRailways(), [])
	);

	const railways = useMemo(() => {
		if (!railwaysData) return [];
		if (Array.isArray(railwaysData)) return railwaysData;
		if (Array.isArray(railwaysData.objects)) return railwaysData.objects;
		return [];
	}, [railwaysData]);

	const railway = useMemo(() => (
		railways.find(item => slugify(item?.Name || "") === slug)
	), [railways, slug]);

	const railwayId = railway?.Id;
	const name = railway?.Name || "";

	const {data: faqsData, error: faqsError, loading: faqsLoading} = useData(useCallback(
		() => (railwayId ? RaildaysService.getRailwayFaqs(railwayId) : Promise.resolve([])),
		[railwayId]
	));

	const faqTopics = useMemo(() => (
		Array.isArray(faqsData) ?
			faqsData.filter(topic => Array.isArray(topic?.Questions) && topic.Questions.length) :
			[]
	), [faqsData]);

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				mx: {xs: 2, md: 3, lg: 5},
				mb: {xs: 2, md: 3, lg: 5},
				backgroundColor: "#fff",
				borderRadius: 2
			}}>
			<Container display="block" px={{xs: 4, md: 8, lg: 12}} py={{xs: 4, md: 6}}>
				<Flex gap={2}>
					<Box>
						<Breadcrumbs sx={{textTransform: "uppercase", color: "#e2b534"}}>
							<Link color="#e2b534" label="Home" uri="/" variant="body2" />
							<Link color="#e2b534" label="Railways" uri="/railways" variant="body2" />
							{name && <Link color="#e2b534" label={name} uri={`/railways/${slug}`} variant="body2" />}
							<String color="#e2b534" noFlex={true} str="FAQs" />
						</Breadcrumbs>
						<String
							bold={true}
							mt={2}
							noFlex={true}
							str={(name ? `${name} FAQs` : "Railway FAQs")}
							variant="h4" />
						{name && (
							<String
								color="text.secondary"
								mt={1}
								noFlex={true}
								str={`Answers to common questions about visiting ${name}.`} />
						)}
					</Box>

					{railwaysError && (
						<Alert severity="error">
							We could not load this railway right now. Please try again shortly.
						</Alert>
					)}

					{railwaysLoading && (
						<Flex alignItems="center" style={{paddingBlock: "4rem"}}>
							<Loader />
						</Flex>
					)}

					{(!railwaysLoading && !railway) && (
						<Card px={0} py={0} style={{borderRadius: 12, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)"}}>
							<Container display="block" px={0} py={0} style={{padding: CARD_BODY_PADDING}}>
								<String bold={true} mb={2} noFlex={true} str="Railway not found" variant="h5" />
								<String
									color="text.secondary"
									mb={3}
									noFlex={true}
									str="We could not locate that railway. Please return to the list and try again." />
								<Button
									label="Back to railways"
									startIcon={<ArrowBackIcon />}
									uri="/railways"
									variant="text" />
							</Container>
						</Card>
					)}

					{railway && (
						<Card px={0} py={0} style={{borderRadius: 12, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)"}}>
							<Container display="block" px={0} py={0} style={{padding: CARD_BODY_PADDING}}>
								<Flex gap={3}>
									{faqsError && (
										<Alert severity="error">We could not load FAQs right now.</Alert>
									)}
									{faqsLoading && (
										<Flex alignItems="center" style={{paddingBlock: "1.5rem"}}>
											<Loader />
										</Flex>
									)}
									{(!faqsLoading && !faqsError && !faqTopics.length) && (
										<String color="text.secondary" str={`There are no FAQs available for ${name}.`} />
									)}
									{(!faqsLoading && Boolean(faqTopics.length)) && (
										<RailwayFaqs topics={faqTopics} />
									)}
								</Flex>
							</Container>
						</Card>
					)}
				</Flex>
			</Container>
		</Box>
	);
};

export default RailwayFaqsView;
